import * as THREE from 'three';
import { SwordTracker } from '../sword/tracking';
import {
  aimOnEnemy,
  guardFeature,
  matchGuard,
  type Guard,
  type GuardFeature,
} from './guardClassify';
import {
  GUARD_STILL_EXIT_FACTOR,
  GUARD_TUNING_DEFAULT,
  SWORD_BLADE_LENGTH_M,
  SWORD_GRIP_LENGTH_M,
  type DominantHand,
  type GuardTuning,
} from '../config/constants';

export type GuardReading = {
  /** The recognized guardia, or null (fuori guardia). Always null until the pose is settled. */
  guard: Guard | null;
  /** Whether the pose is currently held still long enough to be "in guardia". */
  settled: boolean;
  /** The binned relational feature of the settled pose (null until settled) — for the debug HUD. */
  feature: GuardFeature | null;
  /** Where the blade ray lands on the enemy (live, every frame) — for the aim marker. Null = no aim. */
  aimPoint: THREE.Vector3 | null;
};

export type GuardRecognizerOptions = {
  camera: THREE.Camera;
  dominantHand: DominantHand;
  userHeightCm: number;
  tuning?: GuardTuning;
};

/**
 * Owns RELATIONAL guard recognition behind one interface: samples the sword, gates on stillness
 * (hand AND tip below threshold for the dwell, with enter/exit hysteresis), projects the blade ray
 * onto the enemy (the live aim point), and — once settled — classifies the held pose every frame.
 * The runtime passes the enemy's eye/belt world points each frame (the directrix is computed from
 * them and the headset). VG1: tip-still reads the *smoothed* tip velocity (raw never settles).
 */
export class GuardRecognizer {
  private tracker = new SwordTracker(SWORD_BLADE_LENGTH_M, SWORD_GRIP_LENGTH_M);
  private camera: THREE.Camera;
  private dominantHand: DominantHand;
  private userHeightCm: number;
  private tuning: GuardTuning;

  private prevHand: THREE.Vector3 | null = null;
  private stillMs = 0;
  private settled = false;
  private currentGuard: Guard | null = null;
  private currentFeature: GuardFeature | null = null;
  private currentAim: THREE.Vector3 | null = null;

  // Scratch — transient within `recognize`, never retained.
  private readonly userEyes = new THREE.Vector3();
  private readonly handVel = new THREE.Vector3();

  constructor(opts: GuardRecognizerOptions) {
    this.camera = opts.camera;
    this.dominantHand = opts.dominantHand;
    this.userHeightCm = opts.userHeightCm;
    this.tuning = { ...GUARD_TUNING_DEFAULT, ...opts.tuning };
  }

  reset(): void {
    this.tracker.reset();
    this.prevHand = null;
    this.stillMs = 0;
    this.settled = false;
    this.currentGuard = null;
    this.currentFeature = null;
    this.currentAim = null;
  }

  setUserHeightCm(cm: number): void {
    this.userHeightCm = cm;
  }

  setDominantHand(hand: DominantHand): void {
    this.dominantHand = hand;
  }

  setTuning(patch: Partial<GuardTuning>): void {
    this.tuning = { ...this.tuning, ...patch };
  }

  /** Sample the sword and return the current guard reading, measured against the enemy targets. */
  recognize(
    sword: THREE.Object3D,
    dt: number,
    enemyEyes: THREE.Vector3,
    enemyBelt: THREE.Vector3,
  ): GuardReading {
    const frame = this.tracker.sample(sword, dt);
    if (!frame) return this.reading();

    const handSpeed = this.handSpeed(frame.basePos, dt);
    const tipSpeed = frame.smoothedTipVelocity.length();
    const t = this.tuning;

    if (this.settled) {
      const handExit = t.stillHandEnterMps * GUARD_STILL_EXIT_FACTOR;
      const tipExit = t.stillTipEnterMps * GUARD_STILL_EXIT_FACTOR;
      if (handSpeed > handExit || tipSpeed > tipExit) {
        this.settled = false;
        this.stillMs = 0;
      }
    } else if (handSpeed < t.stillHandEnterMps && tipSpeed < t.stillTipEnterMps) {
      this.stillMs += dt * 1000;
      if (this.stillMs >= t.dwellMs) this.settled = true;
    } else {
      this.stillMs = 0;
    }

    this.camera.getWorldPosition(this.userEyes);
    // Live aim point (for the marker), every frame regardless of settled.
    this.currentAim =
      aimOnEnemy(frame.basePos, frame.bladeAxis, enemyEyes, this.userEyes)?.point ?? null;

    // Classification (bins → guard) only when settled; re-run each frame so a transient self-corrects.
    this.currentFeature = this.settled
      ? guardFeature(
          {
            handPos: frame.basePos,
            bladeAxis: frame.bladeAxis,
            edgeDir: frame.edgeDir,
            enemyEyes,
            enemyBelt,
            userEyes: this.userEyes,
            userHeightM: this.userHeightCm / 100,
            dominantHand: this.dominantHand,
          },
          this.tuning,
        )
      : null;
    this.currentGuard = matchGuard(this.currentFeature);
    return this.reading();
  }

  private reading(): GuardReading {
    return {
      guard: this.currentGuard,
      settled: this.settled,
      feature: this.currentFeature,
      aimPoint: this.currentAim,
    };
  }

  private handSpeed(handPos: THREE.Vector3, dt: number): number {
    if (!this.prevHand) {
      this.prevHand = handPos.clone();
      return 0;
    }
    const speed = this.handVel.copy(handPos).sub(this.prevHand).length() / dt;
    this.prevHand.copy(handPos);
    return speed;
  }
}
